// src/shared/utils/dynamicImport.ts
//
// Vite emits hash-suffixed chunks (e.g. jspdf.es.min-CkcFs33Q.js). After a
// deploy, the previous hashes are gone from the server, so a tab opened
// before the deploy will throw "Failed to fetch dynamically imported module"
// the moment the user triggers the lazy code path (Sentry issue 118338024 —
// PDF download from a stale ReportEdit page).
//
// The conventional fix is to reload the page once when we detect a stale
// chunk error. The session-storage flag prevents an infinite reload loop
// when the failure is real (network down, file genuinely missing).

const RELOAD_FLAG_KEY = '__ainovis_chunk_reloaded__';

const STALE_CHUNK_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  // Safari/Webkit symptom of a half-loaded lazy chunk after a deploy: React
  // resolves the lazy payload but the module object has no `.default`, so
  // accessing it inside resolveLazy() throws a TypeError that is not a
  // network error per se. Recover the same way (reload once).
  /undefined is not an object \(evaluating ['"]?\w*\._result\.default/i,
  /Cannot read propert(?:y|ies) of undefined \(reading ['"]default['"]\)/i,
];

export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  const message =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === 'string'
        ? err
        : (() => {
            try {
              return JSON.stringify(err);
            } catch {
              return String(err);
            }
          })();
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message));
}

function reloadOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) return;
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (private mode, sandbox). Reload anyway
    // — the worst case is one extra refresh, not a loop, because the next
    // page load will have fresh chunk hashes.
  }
  window.location.reload();
}

/**
 * Wrap a dynamic `import()` call so a stale-chunk failure triggers a single
 * page reload instead of bubbling a TypeError to the user.
 *
 * Returns a never-resolving promise on the reload path so the caller's
 * remaining async code does not run before the reload happens.
 */
export async function importWithStaleChunkRecovery<T>(
  loader: () => Promise<T>,
): Promise<T> {
  try {
    const value = await loader();
    // Successful import after a previous reload — clear the flag so the next
    // deploy can recover too.
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {
      /* ignore */
    }
    return value;
  } catch (err) {
    if (isStaleChunkError(err)) {
      reloadOnce();
      // Hold the caller until the reload happens so it does not see a
      // rejected promise and surface the error to the user / Sentry.
      return new Promise<T>(() => undefined);
    }
    throw err;
  }
}

/**
 * Install a global handler that catches stale-chunk errors raised by
 * dynamic imports we did not wrap explicitly (route splitting, third-party
 * lazy components). Idempotent.
 */
let globalHandlerInstalled = false;
export function installStaleChunkReloadHandler(): void {
  if (globalHandlerInstalled || typeof window === 'undefined') return;
  globalHandlerInstalled = true;

  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      reloadOnce();
    }
  });

  window.addEventListener('error', (event) => {
    if (isStaleChunkError(event.error ?? event.message)) {
      event.preventDefault();
      reloadOnce();
    }
  });

  // Vite emits this event when a preloaded module fails to load (typical
  // after a deploy that replaced the chunk hashes). Catching it here means
  // we reload before React even attempts to render the lazy component.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnce();
  });
}
